import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import customFetch from '../../api/customFetch';
import { useAuthState } from '../../context/AuthContext';

const ROLES = [
  { value: 'employee', label: 'עובד', color: '#3498db' },
  { value: 'sector_manager', label: 'מנהל קטגוריה', color: '#9b59b6' },
  { value: 'org_manager', label: 'מנהל ארגון', color: '#e67e22' },
  { value: 'admin', label: 'מנהל מערכת', color: '#e74c3c' },
];

export default function AdminUsersList() {
  const [allUsers, setAllUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuthState();
  const isAdmin =
    currentUser?.role === 'admin' || currentUser?.role === 'org_manager';

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    sector: searchParams.get('sector') || 'all',
    role: 'all',
    search: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, sectorsRes] = await Promise.all([
        customFetch.get('/users/admin/all'),
        customFetch.get('/users/sectors'),
      ]);
      setAllUsers(usersRes.data?.users || []);
      setSectors(sectorsRes.data?.sectors || []);
    } catch (err) {
      if (err.response?.status === 401) nav('/admin/login');
      else setError('שגיאה בטעינת המשתמשים');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = [...allUsers];

    if (filters.status === 'pending')
      filtered = filtered.filter((u) => !u.isApproved && u.role === 'employee');
    else if (filters.status === 'approved')
      filtered = filtered.filter((u) => u.isApproved && u.isActive);
    else if (filters.status === 'blocked')
      filtered = filtered.filter((u) => !u.isActive);

    if (filters.sector !== 'all')
      filtered = filtered.filter((u) => u.sector === filters.sector);
    if (filters.role !== 'all')
      filtered = filtered.filter((u) => u.role === filters.role);

    if (filters.search.trim()) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(s) ||
          u.username?.toLowerCase().includes(s) ||
          u.email?.toLowerCase().includes(s) ||
          u.nationalId?.includes(filters.search)
      );
    }
    return filtered;
  }, [allUsers, filters]);

  const statusCounts = useMemo(
    () => ({
      all: allUsers.length,
      pending: allUsers.filter((u) => !u.isApproved && u.role === 'employee')
        .length,
      approved: allUsers.filter((u) => u.isApproved && u.isActive).length,
      blocked: allUsers.filter((u) => !u.isActive).length,
    }),
    [allUsers]
  );

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleApprove = async (id) => {
    await customFetch.post(`/users/admin/approve/${id}`);
    setAllUsers((users) => users.map((u) =>u._id === id ? { ...u, isApproved: true, isActive: true } : u ));
    showSuccess('המשתמש אושר');
  };

  const handleBlock = async (id) => {
    await customFetch.post(`/users/admin/block/${id}`);
    setAllUsers((users) =>
      users.map((u) => (u._id === id ? { ...u, isActive: false } : u))
    );
    showSuccess('המשתמש נחסם');
  };

  const handleUnblock = async (id) => {
    await customFetch.post(`/users/admin/unblock/${id}`);
    setAllUsers((users) =>
      users.map((u) => (u._id === id ? { ...u, isActive: true } : u))
    );
    showSuccess('החסימה הוסרה');
  };

  const handleReject = async (id, reason) => {
    await customFetch.post(`/users/admin/reject/${id}`, { reason });
    setAllUsers((users) => users.filter((u) => u._id !== id));
    showSuccess('המשתמש נדחה');
  };

  const handleRoleChange = async (id, role, additionalSectors = []) => {
    await customFetch.put(`/users/admin/role/${id}`, {
      role,
      additionalSectors,
    });
    setAllUsers((users) =>
      users.map((u) => (u._id === id ? { ...u, role, additionalSectors } : u))
    );
    showSuccess('התפקיד עודכן');
  };

  const handleSectorChange = async (id, sector) => {
    await customFetch.put(`/users/admin/role/${id}`, { sector });
    setAllUsers((users) =>
      users.map((u) => (u._id === id ? { ...u, sector } : u))
    );
    showSuccess('הסקטור עודכן');
  };

  const handlePasswordReset = async (id) => {
    await customFetch.post(`/users/admin/send-password-reset/${id}`);
    showSuccess('לינק איפוס נשלח');
  };

  
  // console.log('AdminUsersList render', {
  //   allUsers,
  //   allUsersIsArray: Array.isArray(allUsers),
  //   allUsersLength: Array.isArray(allUsers) ? allUsers.length : 'not-array',
  //   sectors,
  //   sectorsIsArray: Array.isArray(sectors),
  //   sectorsLength: Array.isArray(sectors) ? sectors.length : 'not-array',
  //   filters,
  //   filteredUsers,
  //   filteredUsersIsArray: Array.isArray(filteredUsers),
  //   filteredUsersLength: Array.isArray(filteredUsers)
  //     ? filteredUsers.length
  //     : 'not-array',
  // });

  if (loading) {
    return (
      <div className='admin-page'>
        <div className='loading-container'>
          <div className='loading-spinner'></div>
          <span>טוען משתמשים...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='admin-page'>
      <div className='page-header'>
        <h1 className='page-title'>
          <span className='page-title-icon'>👥</span>
          ניהול משתמשים ({filteredUsers.length})
        </h1>
        <button className='admin-btn secondary' onClick={loadData}>
          🔄 רענן
        </button>
      </div>

      {error && (
        <div className='admin-alert error'>
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && <div className='admin-alert success'>{success}</div>}

      {/* פילטרים */}
      <div className='admin-card'>
        <h3 className='card-title'>🔍 סינון</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginTop: 16,
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: 'white',
              }}
            >
              סטטוס
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value }))
              }
              className='admin-select'
            >
              <option value='all'>הכל ({statusCounts.all})</option>
              <option value='pending'>ממתינים ({statusCounts.pending})</option>
              <option value='approved'>
                מאושרים ({statusCounts.approved})
              </option>
              <option value='blocked'>חסומים ({statusCounts.blocked})</option>
            </select>
          </div>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: 'white',
              }}
            >
              סקטור
            </label>
            <select
              value={filters.sector}
              onChange={(e) =>
                setFilters((f) => ({ ...f, sector: e.target.value }))
              }
              className='admin-select'
            >
              <option value='all'>כל הסקטורים</option>
              {sectors.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600,color: 'white' }}
            >
              תפקיד
            </label>
            <select
              value={filters.role}
              onChange={(e) =>
                setFilters((f) => ({ ...f, role: e.target.value }))
              }
              className='admin-select'
            >
              <option value='all'>כל התפקידים</option>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600,color: 'white' }}
            >
              חיפוש
            </label>
            <input
              type='text'
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
              placeholder='שם, מייל, ת.ז...'
              className='admin-input'
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              className='admin-btn secondary'
              onClick={() =>
                setFilters({
                  status: 'all',
                  sector: 'all',
                  role: 'all',
                  search: '',
                })
              }
            >
              איפוס
            </button>
          </div>
        </div>
      </div>

      {/* רשימת משתמשים */}
      {filteredUsers.length === 0 ? (
        <div
          className='admin-card'
          style={{ textAlign: 'center', padding: 40 }}
        >
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</div>
          <div>לא נמצאו משתמשים</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, marginTop: 24 }}>
          {filteredUsers.map((user) => (
            <UserCard
              key={user._id}
              user={user}
              currentUser={currentUser}
              isAdmin={isAdmin}
              sectors={sectors}
              onApprove={() => handleApprove(user._id)}
              onBlock={() => handleBlock(user._id)}
              onUnblock={() => handleUnblock(user._id)}
              onReject={(reason) => handleReject(user._id, reason)}
              onRoleChange={(role, addSectors) =>
                handleRoleChange(user._id, role, addSectors)
              }
              onSectorChange={(sector) => handleSectorChange(user._id, sector)}
              onPasswordReset={() => handlePasswordReset(user._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UserCard({
  user,
  currentUser,
  isAdmin,
  sectors,
  onApprove,
  onBlock,
  onUnblock,
  onReject,
  onRoleChange,
  onSectorChange,
  onPasswordReset,
}) {
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editRole, setEditRole] = useState(user.role);
  const [editSector, setEditSector] = useState(user.sector);
  const [editAddSectors, setEditAddSectors] = useState(
    user.additionalSectors || []
  );

  const isCurrentUser =
    currentUser?._id === user._id || currentUser?.id === user._id;
  const roleInfo = ROLES.find((r) => r.value === user.role) || ROLES[0];
  const status =
    !user.isApproved && user.role === 'employee'
      ? { text: 'ממתין', color: '#f59e0b' }
      : !user.isActive
      ? { text: 'חסום', color: '#ef4444' }
      : { text: 'פעיל', color: '#22c55e' };

  const handleSave = () => {
    if (editRole !== user.role || editAddSectors !== user.additionalSectors) {
      onRoleChange(
        editRole,
        editRole === 'sector_manager' ? editAddSectors : []
      );
    }
    if (editSector !== user.sector && editRole === 'employee') {
      onSectorChange(editSector);
    }
    setShowEditor(false);
  };

  return (
    <div className='admin-card' style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <h3 style={{ margin: 0, color: 'var(--card)' }}>
              {user.fullName || user.username}
            </h3>
            <span
              style={{
                background: roleInfo.color,
                color: 'white',
                padding: '4px 10px',
                borderRadius: 12,
                fontSize: '0.8rem',
              }}
            >
              {roleInfo.label}
            </span>
            <span
              style={{
                background: status.color,
                color: 'white',
                padding: '4px 10px',
                borderRadius: 12,
                fontSize: '0.8rem',
              }}
            >
              {status.text}
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 8,
              marginBottom: 12,
              color: 'var(--admin-text-muted)',
            }}
          >
            <div>
              <strong>משתמש:</strong> {user.username}
            </div>
            <div>
              <strong>אימייל:</strong> {user.email}
            </div>
            <div>
              <strong>סקטור:</strong> {user.sector}
            </div>
            <div>
              <strong>ת.ז:</strong> {user.nationalId}
            </div>
          </div>

          {/* עורך */}
          {showEditor && (
            <div
              style={{
                marginTop: 16,
                padding: 16,
                background: 'var(--admin-bg)',
                borderRadius: 12,
              }}
            >
              <h4
                style={{ margin: '0 0 12px 0', color: 'var(--admin-primary)' }}
              >
                ✏️ עריכת משתמש
              </h4>

              {isAdmin && (
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      color: 'var(--admin-text)',
                      display: 'block',
                      marginBottom: 8,
                      fontWeight: 600,
                      
                    }}
                  >
                    תפקיד
                  </label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className='admin-select'
                  >
                    {ROLES.filter((r) => r.value !== 'admin').map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editRole === 'employee' && (
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      color: 'var(--admin-text)',
                      display: 'block',
                      marginBottom: 8,
                      fontWeight: 600,
                    }}
                  >
                    סקטור
                  </label>
                  <select
                    value={editSector}
                    onChange={(e) => setEditSector(e.target.value)}
                    className='admin-select'
                  >
                    {sectors.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editRole === 'sector_manager' && (
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      color: 'var(--admin-text)',
                      display: 'block',
                      marginBottom: 8,
                      fontWeight: 600,
                    }}
                  >
                    סקטורים לניהול (מעבר ל-{user.sector}):
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {sectors
                      .filter((s) => s !== user.sector)
                      .map((sector) => (
                        <label
                          key={sector}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '6px 12px',
                            background: editAddSectors.includes(sector)
                              ? 'rgba(34, 197, 94, 0.2)'
                              : 'rgba(255,255,255,0.1)',
                            border: editAddSectors.includes(sector)
                              ? '1px solid #22c55e'
                              : '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 20,
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            color: editAddSectors.includes(sector)
                              ? '#22c55e'
                              : 'var(--admin-text)',
                          }}
                        >
                          <input
                            type='checkbox'
                            checked={editAddSectors.includes(sector)}
                            onChange={() => {
                              setEditAddSectors((prev) =>
                                prev.includes(sector)
                                  ? prev.filter((s) => s !== sector)
                                  : [...prev, sector]
                              );
                            }}
                            style={{ display: 'none' }}
                          />
                          {sector} {editAddSectors.includes(sector) && '✓'}
                        </label>
                      ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className='admin-btn primary' onClick={handleSave}>
                  💾 שמור
                </button>
                <button
                  className='admin-btn secondary'
                  onClick={() => setShowEditor(false)}
                >
                  ביטול
                </button>
              </div>
            </div>
          )}

          {/* דחייה */}
          {showReject && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: 'var(--admin-bg)',
                borderRadius: 8,
              }}
            >
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder='סיבת הדחייה...'
                style={{
                  width: '100%',
                  minHeight: 60,
                  marginBottom: 8,
                  padding: 8,
                  borderRadius: 6,
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className='admin-btn danger'
                  onClick={() => {
                    onReject(rejectReason);
                    setShowReject(false);
                  }}
                  disabled={!rejectReason.trim()}
                >
                  אשר דחייה
                </button>
                <button
                  className='admin-btn secondary'
                  onClick={() => setShowReject(false)}
                >
                  ביטול
                </button>
              </div>
            </div>
          )}
        </div>

        {/* כפתורים */}
        {!showEditor && !showReject && !isCurrentUser && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minWidth: 140,
            }}
          >
            {!user.isApproved && user.role === 'employee' && (
              <>
                <button className='admin-btn success' onClick={onApprove}>
                  ✓ אשר
                </button>
                <button
                  className='admin-btn danger'
                  onClick={() => setShowReject(true)}
                >
                  ✗ דחה
                </button>
              </>
            )}
            {user.isApproved &&
              (user.isActive ? (
                <button className='admin-btn danger' onClick={onBlock}>
                  🚫 חסום
                </button>
              ) : (
                <button className='admin-btn success' onClick={onUnblock}>
                  ✓ בטל חסימה
                </button>
              ))}
            <button className='admin-btn primary' onClick={onPasswordReset}>
              🔑 איפוס סיסמה
            </button>
            <button
              className='admin-btn secondary'
              onClick={() => setShowEditor(true)}
            >
              ✏️ עריכה
            </button>
          </div>
        )}

        {isCurrentUser && (
          <div
            style={{
              padding: 12,
              background: 'var(--admin-primary)',
              borderRadius: 8,
              color: 'white',
            }}
          >
            👋 זה אתה
          </div>
        )}
      </div>
    </div>
  );
}
